/**
 * Helpers for the silent-refresh dance.
 *
 *  - `bootRefresh()` runs at cold load. On success it stores the bearer + CSRF
 *    token and returns true; on failure it returns false and the caller
 *    redirects to centralized login.
 *  - `stashDeepLink` / `popDeepLink` keep the intended path across the login
 *    round-trip (sessionStorage — the deep-link PATH only, never the token).
 *  - `redirectToCentralizedLogin()` stashes the deep link and navigates to
 *    the bootstrapped `loginUrl` (used verbatim, DNKADM-0274) with
 *    `?returnUrl=<origin>/auth/return` appended.
 */
import { authApi } from '@/api/auth';
import { ApiError } from '@/api/errors';
import { setAccessToken, setCsrfToken } from './authStore';
import { env } from '@/config/env';
import { getBootstrap } from '@/config/bootstrap';

const DEEP_LINK_KEY = 'dink_admin.deepLink';

export async function bootRefresh(): Promise<boolean> {
  try {
    const body = await authApi.refresh();
    if (body.accessToken) {
      setAccessToken(body.accessToken, body.expiresAt ?? null);
      setCsrfToken(body.csrfToken ?? null);
      return true;
    }
    return false;
  } catch (err) {
    // 401 / 403 / network all mean "not authed" at boot.
    if (err instanceof ApiError) return false;
    return false;
  }
}

export function stashDeepLink(path: string): void {
  // Never stash loop paths — they would bounce the user back to login.
  if (path === '/' || path.startsWith('/auth/')) return;
  try {
    sessionStorage.setItem(DEEP_LINK_KEY, path);
  } catch {
    /* ignore quota / disabled storage */
  }
}

export function popDeepLink(): string | null {
  try {
    const v = sessionStorage.getItem(DEEP_LINK_KEY);
    sessionStorage.removeItem(DEEP_LINK_KEY);
    return v;
  } catch {
    return null;
  }
}

export function clearDeepLink(): void {
  try {
    sessionStorage.removeItem(DEEP_LINK_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * `loginUrl` comes from the bootstrapped platform singleton and is used
 * VERBATIM — it carries no query string, so appending `?returnUrl=` is
 * always correct (DNKADM-0274). Never parsed, rebuilt, or normalized.
 */
export function centralizedLoginUrl(returnUrl: string): string {
  const { loginUrl } = getBootstrap();
  return `${loginUrl}?returnUrl=${encodeURIComponent(returnUrl)}`;
}

export function redirectToCentralizedLogin(currentPath: string): void {
  stashDeepLink(currentPath);
  const returnUrl = `${env.appOrigin}/auth/return`;
  if (typeof window !== 'undefined') {
    window.location.assign(centralizedLoginUrl(returnUrl));
  }
}
